package com.xorrus.trading.lot.relayclient.spa

import com.xorrus.trading.lot.relay.command.PushEvent
import com.xorrus.trading.lot.relay.crypto.WebCryptoProvider
import com.xorrus.trading.lot.relay.engine.ControllerEngineConfig
import com.xorrus.trading.lot.relay.engine.ControllerRelayEngine
import com.xorrus.trading.lot.relay.engine.KtorRelayConnector
import com.xorrus.trading.lot.relay.engine.ManagedControllerClient
import com.xorrus.trading.lot.relay.engine.RequestOutcome
import com.xorrus.trading.lot.relay.protocol.ProtocolCrypto
import com.xorrus.trading.lot.relay.storage.IndexedDbKeyStorage
import com.xorrus.trading.lot.relayclient.facade.LotController
import com.xorrus.trading.lot.relayclient.facade.RelayRequester
import com.xorrus.trading.lot.relayclient.facade.TransitionOutcome
import com.xorrus.trading.lot.relayclient.model.DesktopStatus
import com.xorrus.trading.lot.relayclient.model.LotJson
import com.xorrus.trading.lot.relayclient.model.MMS_STRATEGY_ID
import com.xorrus.trading.lot.relayclient.model.MmsOptions
import com.xorrus.trading.lot.relayclient.model.MmsParams
import com.xorrus.trading.lot.relayclient.model.RemoteStrategyStatus
import com.xorrus.trading.lot.relayclient.model.StrategyDescription
import com.xorrus.trading.lot.relayclient.model.StrategyId
import com.xorrus.trading.lot.relayclient.model.StrategyOptions
import com.xorrus.trading.lot.relayclient.model.StrategyParams
import com.xorrus.trading.lot.relayclient.model.StrategySummary
import com.xorrus.trading.lot.relayclient.model.StrategyUpdate
import com.xorrus.trading.lot.relayclient.model.ValidationCode
import com.xorrus.trading.lot.relayclient.model.ValidationError
import com.xorrus.trading.lot.relayclient.model.validateMms
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.launch
import kotlinx.coroutines.promise
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.serializer
import kotlin.js.Promise

/**
 * SPA-Fassade (Spec §6): schmaler `@JsExport`-Wrapper um den gemeinsamen [LotController].
 * Komplexe Typen reisen als JSON-Strings — die typisierten DTOs und `validate()` sind
 * für Kotlin/JS-Formulare direkt aus `commonMain` nutzbar; diese Klasse ist der
 * Interop-Pfad für reines JavaScript/TypeScript.
 */
@OptIn(ExperimentalJsExport::class)
@JsExport
class LotRemoteClient(label: String) {

    private val provider = WebCryptoProvider()
    private val pc = ProtocolCrypto(provider)
    private val scope = CoroutineScope(SupervisorJob())
    private val storage = IndexedDbKeyStorage(provider)
    private val engine = ControllerRelayEngine(
        pc = pc,
        connector = KtorRelayConnector(scope),
        keyStorage = storage,
        config = ControllerEngineConfig(
            label = label,
            ua = js("(typeof navigator !== 'undefined') ? navigator.userAgent : 'js'").unsafeCast<String>(),
        ),
        scope = scope,
    )
    private val client = ManagedControllerClient(engine, scope)

    private val controller = LotController(object : RelayRequester {
        override val events: Flow<PushEvent> get() = client.events
        override suspend fun request(cmd: String, args: JsonObject): RequestOutcome = client.request(cmd, args)
    })

    private val updateHandlers = mutableListOf<(String) -> Unit>()
    private val desktopUpdateHandlers = mutableListOf<(String) -> Unit>()

    init {
        scope.launch {
            controller.updates().collect { update ->
                val encoded = LotJson.encodeToString(StrategyUpdate.serializer(), update)
                updateHandlers.forEach { it(encoded) }
            }
        }
        scope.launch {
            controller.desktopUpdates().collect { status ->
                val encoded = LotJson.encodeToString(DesktopStatus.serializer(), status)
                desktopUpdateHandlers.forEach { it(encoded) }
            }
        }
    }

    /** true, wenn bereits ein Pairing in IndexedDB liegt. */
    fun hasPairing(): Promise<Boolean> = scope.promise { storage.loadPairing() != null }

    /** Koppelt gegen einen gescannten QR; `onSas` MUSS den Vergleichscode anzeigen (§7.2). */
    fun pair(qrUri: String, onSas: (String) -> Unit): Promise<Boolean> = scope.promise {
        engine.pair(qrUri) { onSas(it) }
        true
    }

    /** Baut die Session auf (Resume-Handshake, transparenter Rekey-Reconnect). */
    fun connect(): Promise<Boolean> = scope.promise {
        client.connect()
        true
    }

    /** JSON-Array von [StrategySummary]. */
    fun strategies(): Promise<String> = scope.promise {
        LotJson.encodeToString(serializer<List<StrategySummary>>(), controller.strategies())
    }

    /** JSON von [StrategyDescription] — defaults, presets, options (§3.1). */
    fun describe(strategyId: String): Promise<String> = scope.promise {
        LotJson.encodeToString(
            serializer<StrategyDescription<StrategyParams, StrategyOptions>>(),
            controller.describe(StrategyId(strategyId)),
        )
    }

    /** JSON von [RemoteStrategyStatus]. */
    fun status(strategyId: String): Promise<String> = scope.promise {
        LotJson.encodeToString(serializer<RemoteStrategyStatus>(), controller.status(StrategyId(strategyId)))
    }

    /** JSON von [DesktopStatus] (TWS-Verbindung) — antwortet auch ohne Strategie-Anbindung. */
    fun desktopStatus(): Promise<String> = scope.promise {
        LotJson.encodeToString(DesktopStatus.serializer(), controller.desktopStatus())
    }

    /**
     * `paramsJson` ist das polymorphe Params-Objekt inkl. `strategyId`-Diskriminator.
     * Ergebnis: JSON `{"outcome":"applied"|"alreadyInTargetState"|"denied"|"staleOptions"|"unknown", …}`.
     * `staleOptions` ⇒ `describe` neu laden (§3.4); `unknown` ⇒ Status-Read lief bereits, kein Retry (§6).
     */
    fun activate(strategyId: String, paramsJson: String): Promise<String> = scope.promise {
        val params = LotJson.decodeFromString(serializer<StrategyParams>(), paramsJson)
        encodeOutcome(controller.activate(StrategyId(strategyId), params))
    }

    fun deactivate(strategyId: String): Promise<String> = scope.promise {
        encodeOutcome(controller.deactivate(StrategyId(strategyId)))
    }

    /** Registriert einen Handler für gedrosselte `strategy.update`-Pushes (JSON von [StrategyUpdate]). */
    fun onUpdate(handler: (String) -> Unit) {
        updateHandlers += handler
    }

    /** Registriert einen Handler für `desktop.update`-Pushes (JSON von [DesktopStatus]). */
    fun onDesktopUpdate(handler: (String) -> Unit) {
        desktopUpdateHandlers += handler
    }

    /** Geordneter Abschluss (E2E-`bye`). */
    fun close(): Promise<Boolean> = scope.promise {
        client.close()
        true
    }

    private fun encodeOutcome(outcome: TransitionOutcome): String = buildJsonObject {
        when (outcome) {
            is TransitionOutcome.Applied -> {
                put("outcome", "applied")
                put("status", LotJson.encodeToJsonElement(serializer<RemoteStrategyStatus>(), outcome.status))
            }
            is TransitionOutcome.AlreadyInTargetState -> {
                put("outcome", "alreadyInTargetState")
                put("status", LotJson.encodeToJsonElement(serializer<RemoteStrategyStatus>(), outcome.status))
            }
            is TransitionOutcome.Denied -> {
                put("outcome", "denied")
                put("code", outcome.code)
                outcome.msg?.let { put("msg", it) }
            }
            is TransitionOutcome.StaleOptions -> {
                put("outcome", "staleOptions")
                put("fieldErrors", LotJson.encodeToJsonElement(serializer<List<ValidationError>>(), outcome.fieldErrors))
            }
            is TransitionOutcome.Unknown -> {
                put("outcome", "unknown")
                outcome.lastStatus?.let {
                    put("lastStatus", LotJson.encodeToJsonElement(serializer<RemoteStrategyStatus>(), it))
                }
            }
        }
    }.toString()
}

/** Die `strategyId` der MMS-Strategie — für Formular-Aufbau in reinem JS. */
@OptIn(ExperimentalJsExport::class)
@JsExport
fun mmsStrategyId(): String = MMS_STRATEGY_ID.value

/**
 * Geteilte Live-Formularvalidierung (Spec §3.2/§6) für reines JS: dieselbe
 * `validate()`-Funktion, die auch desktopseitig autoritativ entscheidet.
 * `paramsJson` wie im Formular (inkl. oder exkl. `strategyId`-Feld), `optionsJson`
 * die per `describe` geladenen [MmsOptions] (oder null vor dem Load ⇒ nur strukturell).
 * Ergebnis: JSON-Array von [ValidationError].
 */
@OptIn(ExperimentalJsExport::class)
@JsExport
fun validateMmsParams(paramsJson: String, optionsJson: String?): String {
    val errors = try {
        val params = LotJson.decodeFromString(MmsParams.serializer(), paramsJson)
        val options = optionsJson?.takeIf { it.isNotBlank() }?.let {
            LotJson.decodeFromString(MmsOptions.serializer(), it)
        }
        validateMms(params, options)
    } catch (e: Exception) {
        listOf(ValidationError(field = "", message = "Params nicht lesbar: ${e.message}", code = ValidationCode.INVALID))
    }
    return LotJson.encodeToString(serializer<List<ValidationError>>(), errors)
}
